import { Fragment, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "@/api";
import { toast } from "sonner";
import { Search, Ban, Eye, Check, Database, ChevronRight, ChevronDown } from "lucide-react";
import { EvidenceList } from "@/components/Evidence";

const GATE_LABEL = {
  target_fit: "Target Fit",
  opportunity_evidence: "Opportunity Evidence",
  knkt_relevance: "KNKT Relevance",
  evidence_quality: "Evidence Quality",
  buyer_identifiability: "Buyer Identifiability",
};
const GATE_STYLES = {
  pass:    "bg-emerald-950/60 text-emerald-300 border-emerald-800/60",
  pending: "bg-amber-950/60 text-amber-300 border-amber-800/60",
  fail:    "bg-rose-950/60 text-rose-300 border-rose-800/60",
};

function QualificationPanel({ q }) {
  if (!q || !q.gates) return null;
  const gateOrder = ["target_fit", "opportunity_evidence", "knkt_relevance", "evidence_quality", "buyer_identifiability"];
  return (
    <div className="space-y-1.5" data-testid="qualification-panel">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Qualification Gates</span>
        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] border font-mono ${q.is_qualified ? GATE_STYLES.pass : "bg-slate-800/60 text-slate-400 border-slate-700"}`} data-testid="qualification-status-badge">
          {q.is_qualified ? "QUALIFIED" : "NOT YET QUALIFIED"}
        </span>
      </div>
      <ul className="space-y-1">
        {gateOrder.map((gk) => {
          const g = q.gates[gk] || { status: "pending", reason: "" };
          return (
            <li key={gk} className="text-[11px] flex items-start gap-2 leading-snug" data-testid={`gate-${gk}`}>
              <span className={`inline-flex shrink-0 px-1.5 py-0.5 rounded text-[9px] font-mono border ${GATE_STYLES[g.status] || GATE_STYLES.pending}`}>
                {g.status.toUpperCase()}
              </span>
              <span className="text-slate-300"><span className="text-slate-400">{GATE_LABEL[gk]}:</span> {g.reason}</span>
            </li>
          );
        })}
      </ul>
      {!q.is_qualified && (
        <p className="text-[10px] text-slate-500 leading-snug border-l-2 border-amber-800/40 pl-2 pt-1" data-testid="qualification-explain">
          Pursue → Create Opportunity is unavailable until all required gates pass. Buyer Identifiability may remain "pending".
        </p>
      )}
    </div>
  );
}

const STATUS_STYLES = {
  qualified: "bg-sky-950/60 text-sky-300 border-sky-800/60",
  researching: "bg-amber-950/60 text-amber-300 border-amber-800/60",
  passed: "bg-slate-800/60 text-slate-400 border-slate-700",
  suppressed: "bg-rose-950/60 text-rose-300 border-rose-800/60",
};

const TIER_STYLES = {
  strong:   "bg-emerald-950/60 text-emerald-300 border-emerald-800/60",
  moderate: "bg-amber-950/60 text-amber-300 border-amber-800/60",
  weak:     "bg-rose-950/60 text-rose-300 border-rose-800/60",
  not_evaluated: "bg-slate-800/60 text-slate-500 border-slate-700",
};

const TIER_LABEL = { strong: "Strong fit", moderate: "Moderate fit", weak: "Weak fit", not_evaluated: "Not evaluated" };

const STATUS_EXPLAIN = {
  qualified:  "Strong or moderate fit — cleared for outreach draft",
  researching:"Awaiting research to score fit",
  passed:     "Weak fit — outside KNKT ICP thresholds",
  suppressed: "Domain on suppression list — send is fail-closed",
};

function Badge({ children, className, testid }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono border ${className}`} data-testid={testid}>
      {children}
    </span>
  );
}

function ProspectDetail({ pid, decide }) {
  const { data: detail, isLoading } = useQuery({
    queryKey: ["prospect", pid],
    queryFn: async () => (await api.get(`/prospects/${pid}`)).data,
  });
  if (isLoading || !detail) return <div className="text-xs text-slate-500 py-2">Loading detail…</div>;
  return (
    <div className="space-y-4" data-testid="prospect-detail-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-slate-200 tracking-tight">{detail.name}</h2>
          <p className="font-mono text-xs text-slate-500">{detail.domain} · {detail.industry}</p>
          {detail._source === "airtable" && (
            <p className="font-mono text-[10px] text-emerald-300 mt-1" data-testid="detail-source-airtable">
              <Database size={9} className="inline mr-1" /> AIRTABLE · {detail.airtable_record_id}
              {detail.city && ` · ${detail.city}, ${detail.state_region || detail.country || ""}`}
            </p>
          )}
        </div>
        <Badge className={STATUS_STYLES[detail.status] || STATUS_STYLES.passed} testid="detail-status-badge">{detail.status}</Badge>
      </div>

      {detail.contact && (
        <div className="text-xs text-slate-400 border border-slate-800 rounded-md p-2.5 bg-slate-950/40">
          <span className="text-slate-200">{detail.contact.name}</span> · {detail.contact.role} · <span className="font-mono">{detail.contact.email}</span>
        </div>
      )}

      <div className="space-y-2" data-testid="fit-panel">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={TIER_STYLES[detail.fit_tier] || TIER_STYLES.not_evaluated} testid="detail-fit-tier">
            {TIER_LABEL[detail.fit_tier] || "Not evaluated"}
          </Badge>
          <span className="font-mono text-[9px] uppercase tracking-widest text-slate-600" title="UI-derived interpretation">ui-derived</span>
          {detail.fit_score != null && (
            <span className="font-mono text-[10px] text-slate-500">
              {detail._source === "airtable" ? `${(detail.fit_score * 100).toFixed(0)}% of min` : `score ${detail.fit_score.toFixed(2)}`}
            </span>
          )}
        </div>
        {detail.fit_headline && <div className="text-sm text-slate-200 leading-snug" data-testid="fit-headline">{detail.fit_headline}</div>}
        <div className="text-[11px] text-slate-500 leading-snug border-l-2 border-slate-700 pl-2">{STATUS_EXPLAIN[detail.status] || ""}</div>
        {detail._source === "airtable" && (
          <div className="text-[10px] font-mono text-slate-600 border-l-2 border-amber-800/40 pl-2 leading-relaxed">
            <span className="text-amber-400">⚠ UI-derived tier</span> — Total Score and Minimum Opportunity Score are authoritative production values.
          </div>
        )}
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 pt-1">Fit reasoning</div>
        {detail.fit_reasoning?.length ? (
          <ul className="space-y-1">
            {detail.fit_reasoning.map((r, i) => (
              <li key={i} className="text-xs text-slate-300 leading-normal pl-3 border-l border-slate-700">{r}</li>
            ))}
          </ul>
        ) : <p className="text-xs text-slate-500">No fit evaluation yet.</p>}
      </div>

      <div className="space-y-1.5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Evidence ({detail.evidence?.length || 0})</div>
        <EvidenceList evidence={detail.evidence} emptyText="No research evidence" />
      </div>

      <QualificationPanel q={detail.qualification} />

      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-800/60">
        {detail.qualification?.is_qualified && (
          <button onClick={() => decide.mutate({ action: "pursue_prospect", target_type: "prospect", target_id: detail.id })} data-testid="prospect-pursue-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-[#065F46] text-[#6EE7B7] border-[#059669] hover:brightness-125 transition-all">
            <Check size={12} /> Pursue → Create Opportunity
          </button>
        )}
        <button onClick={() => decide.mutate({ action: "investigate_prospect", target_type: "prospect", target_id: detail.id, reason: prompt("What specific question needs research?") || undefined })} data-testid="prospect-investigate-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700 transition-colors">
          <Eye size={12} /> Investigate
        </button>
        <button onClick={() => decide.mutate({ action: "pass_prospect", target_type: "prospect", target_id: detail.id })} data-testid="prospect-pass-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-[#7F1D1D] text-[#F87171] border-[#DC2626] hover:brightness-125 transition-all">
          <Ban size={12} /> Pass
        </button>
      </div>
    </div>
  );
}

export default function Prospects() {
  const [search, setSearch] = useState("");
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("selected");
  const qc = useQueryClient();

  const { data: prospects = [], isLoading } = useQuery({
    queryKey: ["prospects"],
    queryFn: async () => (await api.get("/prospects")).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["prospects"] });
    qc.invalidateQueries({ queryKey: ["prospect", selectedId] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const decide = useMutation({
    mutationFn: async (payload) => (await api.post("/decisions", payload)).data,
    onSuccess: () => { toast.success("Decision recorded"); invalidate(); },
    onError: (e) => toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Decision failed"),
  });

  const filtered = prospects.filter((p) =>
    !search || (p.name || "").toLowerCase().includes(search.toLowerCase()) || (p.domain || "").includes(search.toLowerCase())
  );

  const toggle = (id) => setParams(selectedId === id ? {} : { selected: id });

  return (
    <div className="space-y-6" data-testid="prospects-view">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-100">Prospects</h1>
          <p className="text-sm text-slate-400 mt-1">Companies with evidence, fit reasoning and suppression state.</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search company or domain" data-testid="prospect-search-input"
            className="bg-slate-900/80 border border-slate-700 rounded-md pl-9 pr-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500 w-64" />
        </div>
      </div>

      <div className="border border-slate-800 bg-slate-900/60 rounded-lg overflow-hidden">
        <table className="w-full text-left" data-testid="prospects-table">
          <thead>
            <tr className="border-b border-slate-800 font-mono text-[10px] uppercase tracking-widest text-slate-500">
              <th className="px-3 py-2 w-6"></th>
              <th className="px-3 py-2">Company</th>
              <th className="px-3 py-2">Industry</th>
              <th className="px-3 py-2">Fit</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Source</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="px-3 py-4 text-sm text-slate-500">Loading…</td></tr>}
            {filtered.map((p) => {
              const open = selectedId === p.id;
              return (
                <Fragment key={p.id}>
                  <tr onClick={() => toggle(p.id)} data-testid={`prospect-row-${p.id}`}
                    className={`text-xs border-b border-slate-800/50 hover:bg-slate-800/40 cursor-pointer font-mono text-slate-300 ${open ? "bg-slate-800/50" : ""}`}>
                    <td className="px-3 py-2 text-slate-500">{open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</td>
                    <td className="px-3 py-2">
                      <div className="font-sans text-sm text-slate-200">{p.name}</div>
                      <div className="text-[11px] text-slate-500">{p.domain}</div>
                    </td>
                    <td className="px-3 py-2">{p.industry}</td>
                    <td className="px-3 py-2">
                      <Badge className={TIER_STYLES[p.fit_tier] || TIER_STYLES.not_evaluated} testid={`prospect-fit-tier-${p.id}`}>
                        {TIER_LABEL[p.fit_tier] || "Not evaluated"}
                      </Badge>
                      {p.fit_score != null && (
                        <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                          {p._source === "airtable" ? `${(p.fit_score * 100).toFixed(0)}% of min` : `score ${p.fit_score.toFixed(2)}`}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={STATUS_STYLES[p.status] || STATUS_STYLES.passed} testid={`prospect-status-${p.id}`}>{p.status}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      {p._source === "airtable" ? (
                        <Badge className="bg-emerald-950/40 text-emerald-300 border-emerald-800/60" testid={`prospect-source-${p.id}`}>
                          <Database size={9} className="mr-1" /> AIRTABLE
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-800/60 text-slate-400 border-slate-700" testid={`prospect-source-${p.id}`}>MOCK</Badge>
                      )}
                    </td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={6} className="p-4 bg-slate-950/60 border-b border-slate-800">
                        <ProspectDetail pid={p.id} decide={decide} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
